import { cacheResponse } from '~/composables/useHlsPlayer/videoSegmentCache';

/**
 * Returns the video URL with a cache-buster (`_cb=1`) to avoid stale CORS preflight cache.
 * Ensures URL is absolute — critical for iOS Safari HLS manifest resolution.
 */
export function getProxiedVideoUrl(
	videoUrl: string | null | undefined
): string | null {
	if (videoUrl == null || videoUrl === '') return null;

	// Convert relative URL to absolute (critical for SSR on iOS)
	const absoluteUrl = (() => {
		if (typeof window === 'undefined' || videoUrl.startsWith('http')) {
			return videoUrl;
		}
		try {
			return new URL(videoUrl, window.location.href).href;
		} catch {
			// If URL parsing fails, use as-is
			return videoUrl;
		}
	})();

	const separator = absoluteUrl.includes('?') ? '&' : '?';
	return `${absoluteUrl}${separator}_cb=1`;
}

export interface ThumbnailOptions {
	width?: number;
	height?: number;
	quality?: number;
	format?: string;
}

/**
 * Composable that returns a function to generate optimized thumbnail URLs
 * via NuxtImage's configured provider (e.g. Netlify Image CDN in production).
 * Must be called inside `<script setup>` or another composable.
 */
export function useOptimizedThumbnail() {
	const img = useImage();

	return (
		thumbnailUrl: string | null,
		options: ThumbnailOptions = {}
	): string | null => {
		if (!thumbnailUrl) return null;

		const { width, height, quality = 80, format = 'jpg' } = options;

		return img(thumbnailUrl, {
			format,
			quality,
			...(width && { width }),
			...(height && { height }),
			...((width || height) && { fit: 'cover' })
		});
	};
}

export type VideoOrientation = 'portrait' | 'landscape' | 'square';
export const COMPACT_HERO_VIDEO_MAX_WIDTH = 700;

/**
 * Determines video orientation based on width and height.
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns 'portrait' if height > width, 'landscape' if width > height, 'square' if equal
 */
export function getVideoOrientation(
	width: number | undefined,
	height: number | undefined
): VideoOrientation {
	if (!width || !height || width === 0 || height === 0) {
		// Default to portrait for mobile-first video reels
		return 'portrait';
	}

	if (height > width) return 'portrait';
	if (width > height) return 'landscape';
	return 'square';
}

/**
 * Checks if the video is in portrait orientation (height > width).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is portrait, false otherwise
 */
export function isPortraitVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'portrait';
}

/**
 * Checks if the video is in landscape orientation (width > height).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is landscape, false otherwise
 */
export function isLandscapeVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'landscape';
}

/**
 * Hero videos with smaller widths should use the compact first-slot layout,
 * even if the encoded orientation metadata is not strictly portrait.
 */
export function shouldUseCompactHeroVideoLayout(
	width: number | undefined,
	height: number | undefined
): boolean {
	if (width && width > 0 && width <= COMPACT_HERO_VIDEO_MAX_WIDTH) {
		return true;
	}

	return isPortraitVideo(width, height);
}

// ---------------------------------------------------------------------------
// HLS manifest parsing & segment prefetch helpers
// ---------------------------------------------------------------------------

interface HlsVariant {
	bandwidth: number;
	url: string;
}

/**
 * Resolves a potentially relative URL against a base URL.
 * Follows HLS spec: relative paths inherit the base origin + directory,
 * but NOT query strings or fragments from the parent.
 */
function resolveUrl(base: string, relative: string): string {
	if (relative.startsWith('http://') || relative.startsWith('https://')) {
		return relative;
	}
	const baseUrl = new URL(base);
	if (relative.startsWith('/')) {
		return `${baseUrl.origin}${relative}`;
	}
	const basePath = baseUrl.pathname.substring(
		0,
		baseUrl.pathname.lastIndexOf('/') + 1
	);
	return `${baseUrl.origin}${basePath}${relative}`;
}

/**
 * Parses an HLS master playlist and returns variant streams sorted by bandwidth (ascending).
 * Returns empty array if the playlist is a media playlist (no variants).
 */
function parseMasterPlaylist(text: string): HlsVariant[] {
	const lines = text.split('\n').map(l => l.trim());
	const variants: HlsVariant[] = [];

	for (let i = 0; i < lines.length - 1; i++) {
		const line = lines[i] ?? '';
		if (!line.startsWith('#EXT-X-STREAM-INF:')) continue;

		const bwMatch = line.match(/BANDWIDTH=(\d+)/);
		if (!bwMatch) continue;

		// The URL is the next non-empty, non-comment line
		const url = lines[i + 1] ?? '';
		if (!url || url.startsWith('#')) continue;

		variants.push({ bandwidth: Number(bwMatch[1]), url });
	}

	return variants.sort((a, b) => a.bandwidth - b.bandwidth);
}

/**
 * Parses an HLS media playlist and returns the first N segment URLs.
 */
function parseMediaPlaylist(text: string, maxSegments: number = 1): string[] {
	const lines = text.split('\n').map(l => l.trim());
	const segments: string[] = [];

	for (const line of lines) {
		if (line.startsWith('#') || line === '') continue;
		segments.push(line);
		if (segments.length >= maxSegments) break;
	}

	return segments;
}

/**
 * Extracts the `v=HASH` version parameter from a URL.
 * Shaka's custom scheme handler (`getVersionedUrl`) appends this to all
 * `.m3u8` and `.ts` requests, so our prefetched URLs must include it too
 * for the browser/CDN cache to hit.
 */
function extractVersionParam(url: string): string {
	const match = url.match(/[?&]v=([^&]+)/);
	return match?.[1] ?? '';
}

/** Appends `?v=HASH` to a URL if a version hash is available. */
function appendVersion(url: string, versionHash: string): string {
	if (!versionHash) return url;
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}v=${versionHash}`;
}

/**
 * Fetches a URL and stores the response in the shared CacheStorage so that
 * Shaka Player's scheme handler finds it and skips the network request.
 * Clones the response upfront so the caller can safely consume the original.
 */
async function fetchAndCache(
	url: string,
	fetchOpts: RequestInit
): Promise<Response> {
	const response = await fetch(url, fetchOpts);
	// Clone immediately — cacheResponse is async and may run after the caller
	// has already consumed the original body.
	cacheResponse(url, response.clone()).catch(() => {});
	return response;
}

/**
 * Prefetches the first segment of the lowest-quality variant for an HLS video.
 * Responses are stored in CacheStorage (`bifrost-video-segments`) so that
 * Shaka Player's custom scheme handler reuses them instead of re-downloading.
 *
 * Chain: master.m3u8 → lowest variant playlist → first segment
 */
export async function prefetchFirstSegment(
	masterUrl: string,
	signal: AbortSignal
): Promise<void> {
	const fetchOpts = { priority: 'low', signal } as RequestInit;

	// Extract version hash from the master URL (e.g. master.m3u8?v=HASH&_cb=1)
	// This must be propagated to all sub-requests so the cache keys match
	// what Shaka Player's getVersionedUrl() will produce.
	const versionHash = extractVersionParam(masterUrl);

	// 1. Fetch & parse master playlist — also cache for Shaka reuse
	const masterResponse = await fetchAndCache(masterUrl, fetchOpts);
	const masterText = await masterResponse.text();
	const variants = parseMasterPlaylist(masterText);

	if (variants.length === 0) {
		// Single-variant stream — master IS the media playlist
		const segments = parseMediaPlaylist(masterText, 1);
		const firstSegment = segments[0];
		if (firstSegment) {
			const segmentUrl = appendVersion(
				resolveUrl(masterUrl, firstSegment),
				versionHash
			);
			await fetchAndCache(segmentUrl, fetchOpts);
		}
		return;
	}

	// 2. Pick the lowest bandwidth variant
	const lowestVariant = variants[0];
	if (!lowestVariant) return;
	const variantUrl = appendVersion(
		resolveUrl(masterUrl, lowestVariant.url),
		versionHash
	);

	// 3. Fetch & parse variant playlist — also cache for Shaka reuse
	const variantResponse = await fetchAndCache(variantUrl, fetchOpts);
	const variantText = await variantResponse.text();
	const segments = parseMediaPlaylist(variantText, 1);

	const firstSegment = segments[0];
	if (!firstSegment) return;

	// 4. Prefetch the first segment with the same version param Shaka will use
	const segmentUrl = appendVersion(
		resolveUrl(variantUrl, firstSegment),
		versionHash
	);
	await fetchAndCache(segmentUrl, fetchOpts);
}

/**
 * Composable for video URL management.
 * Provides reactive utilities for video URLs.
 */
export function useVideoUrls() {
	return {
		getProxiedVideoUrl,
		getOptimizedThumbnailUrl: useOptimizedThumbnail(),
		getVideoOrientation,
		isPortraitVideo,
		isLandscapeVideo,
		prefetchFirstSegment
	};
}
